import React, { useMemo, useRef, useState } from 'react';
import Sliders from '@uber/base-icons/sliders';
import XSmall from '@uber/base-icons/x-small';
import { useStyletron } from 'baseui';
import { Button, KIND, SHAPE, SIZE } from 'baseui/button';
import { ButtonGroup } from 'baseui/button-group';
import { Checkbox } from 'baseui/checkbox';
import { StatefulMenu, StyledListItem } from 'baseui/menu';
import { useAtom, useAtomValue } from 'jotai';
import { useOnceEffect } from '../../../modernization-common/hooks/use-once-effect';
import { FILTER_ACTION } from '../../review-side-panel/constants';
import { annotationLabelsAtom, selectedLabelAtom } from '../atoms';
import { useAnnotationVisibilityHandler } from '../hooks/use-annotation-visibility-handler';
import useAnalyticsForReviewSidePanel from '../hooks/useAnalyticsForReviewSidePanel';
export const FilterBar = () => {
  const [css, theme] = useStyletron();
  const [selectedLabels, setSelectedLabels] = useAtom(selectedLabelAtom);
  const annotationLabels = useAtomValue(annotationLabelsAtom);
  const filterMenuRef = useRef<HTMLDivElement>(null);
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState<boolean>(false);
  const {
    toggleAnnotationVisibilityCallback
  } = useAnnotationVisibilityHandler();
  const {
    sendEventReviewFilter,
    sendEventReviewAnnotationsVisibility
  } = useAnalyticsForReviewSidePanel();
  const FILTER_OPTIONS = useMemo(() => [{
    id: 'all',
    label: 'All'
  }, ...annotationLabels.map(label => ({
    id: label,
    label
  }))], [annotationLabels]);
  const isAllSelected = useMemo(() => annotationLabels.length > 0 && annotationLabels.every(label => selectedLabels.includes(label)), [annotationLabels, selectedLabels]);
  const handleFilterClick = () => {
    setIsFilterMenuOpen(!isFilterMenuOpen);
  };
  const handleFilterSelect = (item: {
    id: string;
    label: string;
  }) => {
    if (item.id === 'all') {
      const wasSelected = isAllSelected;
      setSelectedLabels(wasSelected ? [] : annotationLabels);
      sendEventReviewFilter({
        filterValue: 'ALL',
        filterAction: wasSelected ? FILTER_ACTION.REMOVE : FILTER_ACTION.ADD
      });
      sendEventReviewAnnotationsVisibility({
        value: wasSelected ? 'HIDE' : 'SHOW_ALL'
      });
      if (toggleAnnotationVisibilityCallback) {
        toggleAnnotationVisibilityCallback({
          allLabels: true,
          hidden: wasSelected
        });
      }
    } else {
      const wasSelected = selectedLabels.includes(item.id);
      setSelectedLabels(prev => wasSelected ? prev.filter(id => id !== item.id) : [...prev, item.id]);
      sendEventReviewFilter({
        filterValue: (item.id as any),
        filterAction: wasSelected ? FILTER_ACTION.REMOVE : FILTER_ACTION.ADD
      });
      if (toggleAnnotationVisibilityCallback) {
        toggleAnnotationVisibilityCallback({
          label: item.id,
          hidden: wasSelected
        });
      }
    }
  };

  // Handle the filter state when the component mounts as while coming back to review mode from edit mode, all annotations are visible but filter state is preserved, so need to hide the labels according to the filter state
  useOnceEffect(() => {
    if (toggleAnnotationVisibilityCallback) {
      // If all labels are selected then don't need to hide any labels
      if (isAllSelected) {
        return;
      }
      const labelsToHide = annotationLabels.filter(label => !selectedLabels.includes(label));
      labelsToHide.forEach(label => {
        toggleAnnotationVisibilityCallback({
          label,
          hidden: true
        });
      });
    }
  });
  return <div className={css({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: theme.sizing.scale600,
    gap: theme.sizing.scale600,
    position: 'relative'
  })} ref={filterMenuRef}>
      <ButtonGroup shape={SHAPE.pill} size={SIZE.small} overrides={{
      Root: {
        style: {
          overflow: 'auto',
          whiteSpace: 'nowrap'
        }
      }
    }}>
        {selectedLabels.map(label => <Button key={label} endEnhancer={() => <XSmall onClick={() => handleFilterSelect({
        id: label,
        label
      })} />}>
            {label}
          </Button>)}
      </ButtonGroup>
      <Button kind={KIND.secondary} shape={SHAPE.pill} size={SIZE.small} overrides={{
      BaseButton: {
        style: {
          backgroundColor: theme.colors.backgroundInversePrimary,
          color: theme.colors.contentInversePrimary
        }
      }
    }} startEnhancer={() => <Sliders />} onClick={handleFilterClick} data-testid="filter-button">
        {`(${selectedLabels.length})`}
      </Button>
      {isFilterMenuOpen && <StatefulMenu items={FILTER_OPTIONS} rootRef={filterMenuRef} onItemSelect={({
      item
    }) => handleFilterSelect(item)} overrides={{
      List: {
        style: {
          position: 'absolute',
          top: `calc(100% + ${theme.sizing.scale600})`,
          right: 0,
          maxHeight: theme.sizing.scale4800,
          overflow: 'auto'
        }
      },
      Option: {
        component: ({
          item,
          ...props
        }) => {
          const isChecked = item.id === 'all' ? isAllSelected : selectedLabels.includes(item.id);
          const isDisabled = item.id === 'all' && annotationLabels.length === 0;
          return <StyledListItem {...props}>
                    <Checkbox checked={isChecked} disabled={isDisabled} onChange={() => handleFilterSelect(item)} overrides={{
              Root: {
                style: {
                  alignItems: 'center'
                }
              },
              Checkmark: {
                style: ({
                  $checked
                }) => ({
                  height: theme.sizing.scale600,
                  width: theme.sizing.scale600,
                  borderRadius: theme.borders.radius200,
                  borderColor: theme.colors.contentTertiary,
                  backgroundColor: $checked ? theme.colors.contentTertiary : null
                })
              }
            }}>
                      {item.label}
                    </Checkbox>
                  </StyledListItem>;
        }
      }
    }} />}
    </div>;
};